import React, { useState, useEffect } from 'react';
import { useMap } from '@vis.gl/react-google-maps';
import { isValidCenter, resolveGoogleMapTypeId, toLatLng } from './mapViewUtils';
import { MAP_DEFAULT_ZOOM } from './mapConfig';

/** Show recenter when zoomed out 1.5+ levels below default */
const RECENTER_ZOOM_THRESHOLD = MAP_DEFAULT_ZOOM - 1.5;

export default function MapControlsBar({
  workCenter,
  homeCenter,
  mapLayer,
  onMapLayerChange,
  onOpenFilters = () => {},
  showBikeLayer = false,
  onBikeLayerChange = () => {},
  showSafetyControls = true,
  showSafetyLayer = false,
  onSafetyLayerChange = () => {},
  safetyOpacity = 0.45,
  onSafetyOpacityChange = () => {},
  isFullscreen = false,
  onFullscreenToggle = () => {},
}) {
  const map = useMap();
  const hasWork = isValidCenter(workCenter);
  const hasHome = isValidCenter(homeCenter);
  const hasRecenter = hasWork || hasHome;
  const [layerMenuOpen, setLayerMenuOpen] = useState(false);
  const [showRecenterButton, setShowRecenterButton] = useState(false);

  useEffect(() => {
    if (!layerMenuOpen) return undefined;
    const closeMenu = () => setLayerMenuOpen(false);
    window.addEventListener('click', closeMenu);
    return () => window.removeEventListener('click', closeMenu);
  }, [layerMenuOpen]);

  // Show recenter only when primary location (blue dot) is off-screen or zoomed out 2+ levels
  useEffect(() => {
    if (!hasRecenter || !map) {
      setShowRecenterButton(false);
      return undefined;
    }
    const primary = hasWork ? workCenter : homeCenter;
    const latLng = toLatLng(primary);

    const update = () => {
      const bounds = map.getBounds();
      const zoom = map.getZoom();
      if (bounds == null || typeof zoom !== 'number') {
        setShowRecenterButton(false);
        return;
      }
      const inBounds = bounds.contains(latLng);
      const zoomedOutTooFar = zoom <= RECENTER_ZOOM_THRESHOLD;
      setShowRecenterButton(!inBounds || zoomedOutTooFar);
    };

    update();
    const listeners = [
      map.addListener('bounds_changed', update),
      map.addListener('zoom_changed', update),
    ];
    return () => {
      listeners.forEach((l) => window.google?.maps?.event?.removeListener(l));
    };
  }, [hasRecenter, hasWork, workCenter, homeCenter, map]);

  const handleRecenter = () => {
    if (!map) return;
    if (hasWork && hasHome) {
      const bounds = new window.google.maps.LatLngBounds();
      bounds.extend(toLatLng(workCenter));
      bounds.extend(toLatLng(homeCenter));
      map.fitBounds(bounds, 60);
    } else {
      map.panTo(toLatLng(hasWork ? workCenter : homeCenter));
      map.setZoom(14);
    }
  };

  const handleMapTypeSelect = (layer) => {
    onMapLayerChange(layer);
    if (map) map.setMapTypeId(resolveGoogleMapTypeId(layer));
    setLayerMenuOpen(false);
  };

  return (
    <>
      {/* Top bar: Recenter (top-left, small square) + Icons (top-right) */}
      <div className="map-view__controls-top">
        {hasRecenter && (
          <button
            type="button"
            className={`map-view__control-btn map-view__control-btn--recenter${showRecenterButton ? ' map-view__control-btn--recenter-visible' : ''}`}
            onClick={handleRecenter}
            title="Recenter on work and home"
            aria-label="Recenter on work and home"
            aria-hidden={!showRecenterButton}
          >
            Re-center
          </button>
        )}
        <button
          type="button"
          className="map-view__control-btn map-view__control-btn--amenities"
          onClick={onOpenFilters}
          title="Open filters"
          aria-label="Open filters"
        >
          Filters
        </button>
      </div>

      {/* Bottom bar: Layers (bottom-left) + Fullscreen (bottom-right) */}
      <div className="map-view__controls-bottom">
        <div className="map-view__control-menu map-view__control-menu--layers" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            className="map-view__control-btn map-view__control-btn--layers"
            onClick={() => setLayerMenuOpen((v) => !v)}
            title="Map type"
            aria-expanded={layerMenuOpen}
            aria-haspopup="true"
            aria-label="Map type: switch between Map and Satellite"
          >
            <span className="map-view__control-layers-icon" aria-hidden="true">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2 2 7l10 5 10-5-10-5Z" />
                <path d="M2 17l10 5 10-5" />
                <path d="M2 12l10 5 10-5" />
              </svg>
            </span>
            <span>Layers</span>
          </button>
          {layerMenuOpen && (
            <div className="map-view__control-menu-panel map-view__control-menu-panel--layers" role="menu" aria-label="Map type">
              <button
                type="button"
                role="menuitemradio"
                aria-checked={mapLayer === 'default'}
                className="map-view__control-menu-item map-view__control-menu-item--action"
                onClick={() => handleMapTypeSelect('default')}
              >
                Map
              </button>
              <button
                type="button"
                role="menuitemradio"
                aria-checked={mapLayer === 'satellite'}
                className="map-view__control-menu-item map-view__control-menu-item--action"
                onClick={() => handleMapTypeSelect('satellite')}
              >
                Satellite
              </button>
              <div className="map-view__control-menu-divider" role="separator" />
              <button
                type="button"
                role="menuitemcheckbox"
                aria-checked={showBikeLayer}
                aria-label="Toggle biking layer"
                className={`map-view__icon-toggle map-view__icon-toggle--layers${showBikeLayer ? '' : ' map-view__icon-toggle--inactive'}`}
                onClick={() => onBikeLayerChange(!showBikeLayer)}
              >
                <span className="map-view__icon-swatch map-view__icon-swatch--bike" aria-hidden="true">🚲</span>
                <span>Biking</span>
              </button>

              {showSafetyControls && (
                <>
                  <button
                    type="button"
                    role="menuitemcheckbox"
                    aria-checked={showSafetyLayer}
                    aria-label="Toggle safety layer"
                    className={`map-view__icon-toggle map-view__icon-toggle--layers${showSafetyLayer ? '' : ' map-view__icon-toggle--inactive'}`}
                    onClick={() => onSafetyLayerChange(!showSafetyLayer)}
                  >
                    <span className="map-view__icon-swatch map-view__icon-swatch--safety" aria-hidden="true">🛡️</span>
                    <span>Safety</span>
                  </button>
                  {showSafetyLayer && (
                    <div className="map-view__control-menu-opacity" role="group" aria-label="Safety overlay opacity">
                      <label className="map-view__control-menu-opacity-label" htmlFor="safety-layer-opacity">
                        Safety opacity: {Math.round((safetyOpacity ?? 0.45) * 100)}%
                      </label>
                      <input
                        id="safety-layer-opacity"
                        type="range"
                        min={0}
                        max={1}
                        step={0.05}
                        value={safetyOpacity ?? 0.45}
                        onChange={(e) => onSafetyOpacityChange(Number(e.target.value))}
                        className="map-view__control-menu-opacity-slider"
                        aria-label="Safety overlay opacity"
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* Fullscreen toggle — bottom-right, same style as other control buttons */}
        <button
          type="button"
          className="map-view__control-btn map-view__control-btn--icon map-view__control-btn--fullscreen"
          onClick={onFullscreenToggle}
          aria-label={isFullscreen ? 'Exit fullscreen map' : 'Open fullscreen map'}
          title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
        >
          {isFullscreen ? '⤫' : '⛶'}
        </button>
      </div>
    </>
  );
}
